/**
 * ItineraryContext — the visitor's planning surface for THIS session.
 *
 * Items pass through these states:
 *   suggested   — shown by the agent, not yet committed by visitor
 *   saved       — visitor explicitly added it (card "Save" / agent
 *                 add_to_itinerary verb)
 *   pending_pay — they hit "Book" / agent fired prefill_checkout;
 *                 the cart points at this item
 *   confirmed   — provider checkout completed (iframe-inspector or
 *                 mock-form success)
 *   cancelled   — visitor or provider cancelled
 *
 * Persistence: backed by functions-py /itineraries/v1 (Postgres). Each
 * visitor (guest or authed) gets one auto-bootstrapped itinerary; items
 * read/write through that one row. Embed-specific state (saved vs
 * suggested, embedded_url, detection_patterns, guests, prices, image)
 * round-trips through `display_data` so the wire shape stays standard
 * functions-py while the iframe restores everything it needs.
 *
 * Mutations are optimistic: state updates synchronously, the API call
 * fires in the background, and on failure we log + reload from server.
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { CheckoutCartItem, DetectionPatterns } from './CheckoutContext';
import { useAuth } from '../hooks/useAuth';
import { useInstance } from './InstanceContext';
import {
  createItinerary,
  createItineraryItem,
  deleteItineraryItem,
  fromWire,
  getItineraryWithItems,
  listItineraries,
  shareItinerary as shareItineraryApi,
  createInvite as createInviteApi,
  joinItinerary as joinItineraryApi,
  listMembers as listMembersApi,
  listMemberships as listMembershipsApi,
  toCreatePayload,
  toUpdatePayload,
  updateItinerary,
  updateItineraryItem,
  type EmbedMember,
  type EmbedItineraryRole,
  type EmbedTripSummary,
} from '../api/itineraryApi';
import logger from '../utils/logger';

export type ItineraryItemStatus =
  | 'suggested'
  | 'saved'
  | 'pending_pay'
  | 'confirmed'
  | 'cancelled';

/** Visitor emphasis on an item, orthogonal to status. Absent = plain saved. */
export type ItineraryItemPriority = 'must_do' | 'maybe';

/** Collaboration scope: the shared plan vs one member's own pick. */
export type ItineraryItemScope = 'group' | 'personal';

export interface ItineraryItem {
  /** Stable id; for server-backed items this is the UUID from the DB. */
  id: string;
  /** Display title — "Old Lahaina Lu‘au". */
  title: string;
  /** Catalog activity id (provider-prefixed when known). */
  activity_id?: string;
  /** Provider canonical id (lowercase). */
  provider?: string;
  /** Optional supplier id — for chokepoint principal binding. */
  external_supplier_id?: string;
  /** ISO date if scheduled (YYYY-MM-DD). */
  date?: string;
  /** Per-tier guest counts as visited at booking time. */
  guests?: { type: string; count: number; tier_id?: string | number }[];
  /** Per-person price when known. */
  unit_price_cents?: number;
  currency_code?: string;
  /** Optional hero image. */
  image_url?: string;
  /** Free-form short description for list rendering. */
  description?: string;
  /** Geo when known — used by future Trip-day timeline view. */
  location_name?: string;
  status: ItineraryItemStatus;
  /** Visitor emphasis (must_do | maybe). Absent = plain saved. Orthogonal
   *  to status — a confirmed booking can still be a must-do. */
  priority?: ItineraryItemPriority;
  /** Collaboration scope: 'group' (shared plan, default) or 'personal'
   *  (this member's own pick, badged by added_by). */
  scope?: ItineraryItemScope;
  /** auth.users id of whoever added the item (personal-pick badge). */
  added_by?: string;
  /** When `status='confirmed'`, the provider's confirmation code. */
  confirmation_code?: string;
  /** When pending_pay, the URL the cart should iframe. */
  embedded_url?: string;
  /** Iframe-inspector rules carried from /catalog/v1/checkout-build. */
  detection_patterns?: DetectionPatterns;
  /** Server-resolved checkout method (iframe | redirect | wg_internal). */
  booking_method?: string;
  /** Audit timestamps (ISO). */
  added_at: string;
  updated_at: string;
}

/** Lightweight subset accepted by `addItem` so callers don't need to mint
 * the full record. id/added_at/updated_at default. status defaults to
 * 'saved' but callers can override (e.g. agent's add_to_itinerary uses
 * 'suggested' until the visitor explicitly commits). */
export interface ItineraryItemInput
  extends Partial<
    Omit<ItineraryItem, 'id' | 'added_at' | 'updated_at' | 'status'>
  > {
  title: string;
  status?: ItineraryItemStatus;
}

/** Visitor's trip-wide identity + party defaults. Captured once via the
 * checkout review or the Trip view's profile card and reused across every
 * booking — no need to re-type name/email/party every time.
 *
 * Persisted server-side in `itinerary.metadata.trip_profile` so it
 * survives reloads and (for authed users) follows them across devices. */
export interface TripProfile {
  contact?: {
    name_first?: string;
    name_last?: string;
    email?: string;
    phone?: string;
  };
  party?: {
    adults: number;
    children: number;
  };
  /** Default trip date when a card doesn't carry its own. ISO YYYY-MM-DD. */
  default_date?: string;
}

interface ItineraryContextValue {
  items: ItineraryItem[];
  /** Server copy fetched at least once (even if empty). */
  hydrated: boolean;
  addItem: (input: ItineraryItemInput) => ItineraryItem;
  /** Adds parked while the visitor registers (auth-gated Add to trip —
   *  see useGatedAddItem). Flushed after the guest→user upgrade. */
  pendingAdds: ItineraryItemInput[];
  queuePendingAdd: (input: ItineraryItemInput) => void;
  /** addItem() every parked add (post-registration); returns the count. */
  flushPendingAdds: () => number;
  clearPendingAdds: () => void;
  /** Update by id; merges patch onto the existing record. */
  updateItem: (id: string, patch: Partial<ItineraryItem>) => void;
  /** Move an item to pending_pay, materialise a CheckoutCartItem from it. */
  startCheckout: (id: string) => CheckoutCartItem | null;
  /** Mark confirmed (called by iframe inspector / mock-form success). */
  markConfirmed: (id: string, confirmation_code?: string) => void;
  removeItem: (id: string) => void;
  /** Counts by status for badge rendering. */
  counts: Record<ItineraryItemStatus, number>;
  pendingCount: number;
  /** Trip title (e.g. "My Oʻahu Wishlist"). Defaults until server hydrate. */
  title: string;
  /** Rename the trip; persists title + stamps metadata.title_user_set. */
  renameItinerary: (title: string) => void;
  /** Read-only share token; null until minted. */
  shareToken: string | null;
  /** Mint (or return) the share token; rotate regenerates it. */
  shareItinerary: (rotate?: boolean) => Promise<string | null>;
  /** The current visitor's user id (guest or registered), for matching
   *  added_by → "You". Null before auth settles. */
  myUserId: string | null;
  /** The caller's role on the active trip (owner for their own bootstrapped
   *  trip; editor/viewer once they join someone else's). */
  myRole: EmbedItineraryRole;
  /** Roster of the active trip (owner + members); loaded by refreshMembers. */
  members: EmbedMember[];
  /** Load the roster for the active trip and settle myRole. */
  refreshMembers: () => Promise<EmbedMember[]>;
  /** Mint a collaboration invite link with a role; returns the token. */
  createInviteLink: (role: 'editor' | 'viewer') => Promise<string | null>;
  /** Promote a personal pick to the group plan (or demote). */
  setItemScope: (id: string, scope: ItineraryItemScope) => void;
  /** Claim an invite token; switches to the joined trip when the instance
   *  enables multi-trip (else records membership without switching). */
  joinTrip: (
    token: string,
  ) => Promise<{ role: EmbedItineraryRole; switched: boolean } | null>;
  /** Whether this instance supports multiple trips + switching. */
  switcherEnabled: boolean;
  /** Trips the visitor can switch between (owned + joined). */
  availableTrips: EmbedTripSummary[];
  /** Load the visitor's trips for the switcher. */
  refreshTrips: () => Promise<EmbedTripSummary[]>;
  /** Switch the active trip. */
  switchTrip: (itineraryId: string) => Promise<void>;
  /** Trip-wide profile (contact + party defaults). Reused across bookings. */
  tripProfile: TripProfile;
  /** Patch the profile and persist to itinerary.metadata.trip_profile. */
  updateTripProfile: (patch: Partial<TripProfile>) => void;
}

const ItineraryContext = createContext<ItineraryContextValue | undefined>(
  undefined,
);

/** Temp client-side id for optimistic items before the server returns one. */
function tempId(): string {
  return `tmp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

const DEFAULT_TITLE = 'My Trip';
// Titles we may safely replace with a host-supplied one (data-wg-trip-title).
// Covers our own default and the brain's auto-create default. A title the
// user typed themselves is never in this set, and metadata.title_user_set
// guards it explicitly once rename UI exists.
const REPLACEABLE_TITLES = new Set([DEFAULT_TITLE, 'My Hawaiʻi trip']);

export const ItineraryProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { sessionInfo, authState } = useAuth();
  const userId = sessionInfo?.userId || null;
  // Public instance token from the URL (?instance=...). Forwarded to
  // backend createItinerary so it can seed the partner's primary listing
  // as a suggested item — turns the first-visit Trips tab into a working
  // demo instead of an empty pane.
  const {
    instanceId: partnerInstanceToken,
    tripTitle: hostTripTitle,
    config,
  } = useInstance();
  // Multi-trip switching is an instance capability. OFF (e.g. wg-explore) keeps
  // the embed strictly single-trip: no switcher UI, and joining a trip does not
  // hijack the active one.
  const switcherEnabled = config?.feature_flags?.trip_switcher === true;

  const [items, setItems] = useState<ItineraryItem[]>([]);
  // True once the SERVER copy has been fetched (even if it was empty). Hosts need
  // this to tell "the trip is genuinely empty" from "we have not looked yet" — the
  // two cases look identical in `items` and must not be treated the same.
  const [hydrated, setHydrated] = useState(false);
  const [itineraryId, setItineraryId] = useState<string | null>(null);
  const [title, setTitle] = useState<string>(DEFAULT_TITLE);
  // Read-only share handle; null until the owner mints a link (or a returning
  // owner hydrates an already-shared trip). Flows up to hosts via SyncReporter.
  const [shareToken, setShareToken] = useState<string | null>(null);
  // Collaboration: the caller's role on the ACTIVE itinerary (owner for their
  // own bootstrapped trip; editor/viewer once they join someone else's), and
  // the roster. Loaded lazily — refreshMembers() is called when the collab UI
  // opens, not on every itinerary load.
  const [myRole, setMyRole] = useState<EmbedItineraryRole>('owner');
  const [members, setMembers] = useState<EmbedMember[]>([]);
  // Trips the visitor can switch between (owned + joined) — only populated when
  // the switcher is enabled for the instance.
  const [availableTrips, setAvailableTrips] = useState<EmbedTripSummary[]>([]);
  const [tripProfile, setTripProfile] = useState<TripProfile>({});
  // Hold the full metadata bag so updateTripProfile patches don't drop
  // fields the brain or other surfaces wrote.
  const itineraryMetadataRef = useRef<Record<string, unknown>>({});
  // Track temp→real id so a refresh that lands while an addItem POST is
  // still in-flight doesn't double-render the item.
  const tempIdMapRef = useRef<Map<string, string>>(new Map());
  // Bootstrapped per (userId) so re-auth swaps the trip without leaking the
  // previous visitor's items.
  const bootstrappedForRef = useRef<string | null>(null);
  // Which user the CURRENT itineraryId belongs to. Set synchronously at
  // bootstrap so consumers (the pending-add flush below) can tell a stale
  // itineraryId from one owned by the signed-in user — React state alone
  // can't: on guest→account sign-in the flush effect fires in the same
  // commit as the userId change, before any reset lands.
  const itineraryOwnerRef = useRef<string | null>(null);

  // Reset when the visitor identity drops (logout / session cleared) OR
  // switches to a DIFFERENT user (sign-in to an existing account — the
  // guest's itinerary must not linger or adds land on an itinerary the new
  // session doesn't own).
  const prevUserIdRef = useRef<string | null>(null);
  useEffect(() => {
    const prev = prevUserIdRef.current;
    prevUserIdRef.current = userId;
    if (userId && (!prev || prev === userId)) return;
    bootstrappedForRef.current = null;
    itineraryOwnerRef.current = null;
    setItineraryId(null);
    setItems([]);
    setTitle(DEFAULT_TITLE);
    setShareToken(null);
    setMyRole('owner');
    setMembers([]);
    setAvailableTrips([]);
    setTripProfile({});
    itineraryMetadataRef.current = {};
    tempIdMapRef.current.clear();
  }, [userId]);

  // Bootstrap: list user's itineraries → create if none → load items.
  useEffect(() => {
    if (!userId) return;
    if (authState !== 'guest' && authState !== 'authenticated') return;
    if (bootstrappedForRef.current === userId) return;
    bootstrappedForRef.current = userId;

    let cancelled = false;
    let completed = false;
    (async () => {
      try {
        const list = await listItineraries();
        let id: string | undefined;
        if (list.length > 0 && list[0]?.id) {
          id = list[0].id;
          logger.info(`[itinerary] using existing itinerary ${id}`);
        } else {
          const created = await createItinerary(
            hostTripTitle || DEFAULT_TITLE,
            partnerInstanceToken,
          );
          id = created?.id;
          if (created?.title) setTitle(created.title);
          logger.info(
            `[itinerary] created new itinerary ${id}` +
              (partnerInstanceToken ? ' (with partner seed)' : ''),
          );
        }
        if (cancelled) return;
        itineraryOwnerRef.current = userId;
        if (!id) {
          // Defensive: a Caddy mis-route used to silently return
          // {status:"ok"} for the bare /itineraries/v1 path, which made us
          // chase GET /itineraries/v1/undefined forever. If that ever
          // happens again we want a loud error instead.
          logger.warn(
            '[itinerary] bootstrap got no itinerary id from server (bad route or empty response)',
          );
          bootstrappedForRef.current = null;
          return;
        }
        setItineraryId(id);

        const { itinerary, items: wire } = await getItineraryWithItems(id);
        if (cancelled) return;
        const meta =
          (itinerary?.metadata as Record<string, unknown> | undefined) || {};
        itineraryMetadataRef.current = { ...meta };
        const profile = (meta.trip_profile as TripProfile | undefined) || {};
        setTripProfile(profile);
        if (itinerary?.title) setTitle(itinerary.title);
        setShareToken(itinerary?.share_token ?? null);

        // One-time host rename: an itinerary that still carries a default
        // title adopts the host's (localized) one. Fire-and-forget — a
        // failure just leaves the default in place until the next bootstrap.
        if (
          hostTripTitle &&
          itinerary &&
          itinerary.title !== hostTripTitle &&
          REPLACEABLE_TITLES.has(itinerary.title) &&
          !meta.title_user_set
        ) {
          setTitle(hostTripTitle);
          updateItinerary(id, { title: hostTripTitle }).catch(e =>
            logger.warn('[itinerary] host trip-title rename failed', e),
          );
        }
        const decoded = wire.map(fromWire);
        // Merge: keep optimistic in-memory items not yet acknowledged
        // (those without a real DB id) so nothing flashes away.
        setItems(prev => {
          const seen = new Set(decoded.map(d => d.id));
          const optimistic = prev.filter(
            p => p.id.startsWith('tmp_') && !seen.has(p.id),
          );
          return [...decoded, ...optimistic];
        });
        logger.info(
          `[itinerary] hydrated ${decoded.length} item(s) from server`,
        );
        setHydrated(true);
        completed = true;
      } catch (e) {
        logger.warn('[itinerary] bootstrap failed', e);
        bootstrappedForRef.current = null; // allow retry on next auth tick
      }
    })();

    return () => {
      cancelled = true;
      // Dep change (e.g. authState guest→authenticated mid-flight) cancels
      // this run; without clearing the guard the re-run early-returns and
      // itineraryId never lands — stalls the pending-add flush forever.
      if (!completed) bootstrappedForRef.current = null;
    };
  }, [userId, authState]);

  // Registration-gated adds: parked until the guest registers (or signs
  // in) IN THE OPEN AUTH SHEET, then flushed into whichever itinerary that
  // account owns. EPHEMERAL by design (Michael, 2026-07-02): an add that
  // never got a registration is a dropped intent — it must not survive a
  // refresh or an abandoned sheet, and must never render as "added"
  // anywhere. AuthView clears the queue when it unmounts unauthenticated.
  // Dedup on (provider, activity_id, title) so a re-tap while the sheet is
  // open doesn't double-queue.
  const [pendingAdds, setPendingAdds] = useState<ItineraryItemInput[]>([]);
  const queuePendingAdd = useCallback((input: ItineraryItemInput) => {
    logger.info('[itinerary] queuePendingAdd', { title: input.title });
    setPendingAdds(prev =>
      prev.some(
        p =>
          p.title === input.title &&
          (p.activity_id ?? '') === (input.activity_id ?? '') &&
          (p.provider ?? '') === (input.provider ?? ''),
      )
        ? prev
        : [...prev, input],
    );
  }, []);
  const clearPendingAdds = useCallback(() => setPendingAdds([]), []);

  const addItem = useCallback(
    (input: ItineraryItemInput): ItineraryItem => {
      logger.info('[itinerary] addItem', {
        title: input.title,
        activity_id: input.activity_id,
        provider: input.provider,
      });
      const now = new Date().toISOString();
      const optimistic: ItineraryItem = {
        id: tempId(),
        added_at: now,
        updated_at: now,
        status: input.status ?? 'saved',
        priority: input.priority,
        scope: input.scope,
        // Optimistic provenance so a personal pick badges immediately; the
        // server re-stamps added_by authoritatively on the create response.
        added_by: input.added_by ?? userId ?? undefined,
        title: input.title,
        activity_id: input.activity_id,
        provider: input.provider,
        external_supplier_id: input.external_supplier_id,
        date: input.date,
        guests: input.guests,
        unit_price_cents: input.unit_price_cents,
        currency_code: input.currency_code,
        image_url: input.image_url,
        description: input.description,
        location_name: input.location_name,
        embedded_url: input.embedded_url,
        detection_patterns: input.detection_patterns,
        booking_method: input.booking_method,
        confirmation_code: input.confirmation_code,
      };

      // Idempotent on (provider, activity_id, date) — agent firing
      // add_to_itinerary twice shouldn't double the row.
      let existingId = '' as string;
      setItems(prev => {
        if (optimistic.activity_id && optimistic.provider) {
          const i = prev.findIndex(
            x =>
              x.activity_id === optimistic.activity_id &&
              x.provider === optimistic.provider &&
              (x.date ?? '') === (optimistic.date ?? ''),
          );
          if (i >= 0) {
            existingId = prev[i].id;
            const merged: ItineraryItem = {
              ...prev[i],
              ...optimistic,
              id: prev[i].id,
              added_at: prev[i].added_at,
              updated_at: now,
            };
            const next = prev.slice();
            next[i] = merged;
            return next;
          }
        }
        return [...prev, optimistic];
      });

      // Persist. If we matched an existing row, PUT; otherwise POST.
      if (!itineraryId) {
        logger.warn(
          '[itinerary] addItem: itineraryId not ready, item is optimistic-only until bootstrap finishes',
        );
        return optimistic;
      }
      if (existingId) {
        // Update path — only run if the existing id is a real server id.
        if (!existingId.startsWith('tmp_')) {
          (async () => {
            try {
              const wire = await updateItineraryItem(
                itineraryId,
                existingId!,
                toUpdatePayload(optimistic, optimistic),
              );
              const decoded = fromWire(wire);
              setItems(prev =>
                prev.map(it => (it.id === existingId ? decoded : it)),
              );
            } catch (e) {
              logger.warn('[itinerary] updateItineraryItem failed', e);
            }
          })();
        }
        return optimistic;
      }

      (async () => {
        try {
          const wire = await createItineraryItem(
            itineraryId,
            toCreatePayload(optimistic),
          );
          const decoded = fromWire(wire);
          tempIdMapRef.current.set(optimistic.id, decoded.id);
          setItems(prev =>
            prev.map(it => (it.id === optimistic.id ? decoded : it)),
          );
        } catch (e) {
          logger.warn('[itinerary] createItineraryItem failed', e);
          // Roll the optimistic row back so the UI doesn't show ghosts.
          setItems(prev => prev.filter(it => it.id !== optimistic.id));
        }
      })();
      return optimistic;
    },
    [itineraryId],
  );

  /** Resolve a logical id (which may be a temp id) to whatever the
   * current real id is, via the temp→real map. */
  const resolveId = useCallback((id: string): string => {
    return tempIdMapRef.current.get(id) || id;
  }, []);

  const updateItem = useCallback(
    (id: string, patch: Partial<ItineraryItem>) => {
      let nextItem: ItineraryItem | null = null;
      setItems(prev =>
        prev.map(it => {
          if (it.id !== id) return it;
          const merged = {
            ...it,
            ...patch,
            updated_at: new Date().toISOString(),
          };
          nextItem = merged;
          return merged;
        }),
      );
      const realId = resolveId(id);
      if (!itineraryId || !nextItem || realId.startsWith('tmp_')) return;
      (async () => {
        try {
          const wire = await updateItineraryItem(
            itineraryId,
            realId,
            toUpdatePayload(nextItem!, patch),
          );
          const decoded = fromWire(wire);
          setItems(prev => prev.map(it => (it.id === id ? decoded : it)));
        } catch (e) {
          logger.warn('[itinerary] updateItineraryItem failed', e);
        }
      })();
    },
    [itineraryId, resolveId],
  );

  const startCheckout = useCallback(
    (id: string): CheckoutCartItem | null => {
      // Build the cart from CURRENT state, not from inside the setItems
      // updater. React may invoke an updater after the enclosing function
      // has already returned, so assigning the return value in there made
      // startCheckout hand back null — and every caller does
      // `if (!newCart) return;`, so the Pay button silently did nothing.
      const source = items.find(it => it.id === id);
      if (!source) return null;
      const sourceGuest = source.guests?.[0];
      const result: CheckoutCartItem = {
        activity_name: source.title,
        activity_id: source.activity_id,
        image_url: source.image_url,
        date: source.date,
        unit_price_cents: source.unit_price_cents,
        guests:
          sourceGuest && typeof sourceGuest.count === 'number'
            ? [
                {
                  type: sourceGuest.type,
                  count: sourceGuest.count,
                  tier_id: sourceGuest.tier_id,
                },
              ]
            : undefined,
        currency_code: source.currency_code,
        provider: source.provider,
        external_supplier_id: source.external_supplier_id,
        embedded_url: source.embedded_url,
        detection_patterns: source.detection_patterns,
        booking_method: source.booking_method,
        item_id: source.id,
        // Always route through the review surface so the URL gets rebuilt
        // with the visitor's trip-profile contact. The surface auto-skips
        // the form when the profile is complete, so this is invisible to
        // repeat bookers but ensures the partner page loads pre-populated.
        requires_review: true,
      };
      let nextItem: ItineraryItem | null = null;
      setItems(prev =>
        prev.map(it => {
          if (it.id !== id) return it;
          const merged: ItineraryItem = {
            ...it,
            status: 'pending_pay',
            updated_at: new Date().toISOString(),
          };
          nextItem = merged;
          return merged;
        }),
      );
      const realId = resolveId(id);
      if (itineraryId && nextItem && !realId.startsWith('tmp_')) {
        (async () => {
          try {
            await updateItineraryItem(
              itineraryId,
              realId,
              toUpdatePayload(nextItem!, { status: 'pending_pay' }),
            );
          } catch (e) {
            logger.warn('[itinerary] startCheckout persist failed', e);
          }
        })();
      }
      return result;
    },
    [items, itineraryId, resolveId],
  );

  const markConfirmed = useCallback(
    (id: string, confirmation_code?: string) => {
      updateItem(id, {
        status: 'confirmed',
        confirmation_code,
      });
    },
    [updateItem],
  );

  const removeItem = useCallback(
    (id: string) => {
      const realId = resolveId(id);
      setItems(prev => prev.filter(it => it.id !== id));
      if (!itineraryId || realId.startsWith('tmp_')) return;
      (async () => {
        try {
          await deleteItineraryItem(itineraryId, realId);
        } catch (e) {
          logger.warn('[itinerary] deleteItineraryItem failed', e);
        }
      })();
    },
    [itineraryId, resolveId],
  );

  const renameItinerary = useCallback(
    (nextTitle: string) => {
      const trimmed = nextTitle.trim().slice(0, 120);
      if (!trimmed) return;
      setTitle(trimmed);
      if (!itineraryId) return;
      // Stamp title_user_set so a host's data-wg-trip-title never overrides
      // a name the visitor typed themselves on a later bootstrap.
      const merged = {
        ...itineraryMetadataRef.current,
        title_user_set: true,
      };
      itineraryMetadataRef.current = merged;
      (async () => {
        try {
          await updateItinerary(itineraryId, {
            title: trimmed,
            metadata: merged,
          });
        } catch (e) {
          logger.warn('[itinerary] renameItinerary persist failed', e);
        }
      })();
    },
    [itineraryId],
  );

  // Mint (or return) the read-only share link. Idempotent server-side: repeat
  // taps get the same token; rotate=true regenerates it (revoke + re-share).
  // Returns the token so callers (host bridge) can hand back a URL.
  const shareItinerary = useCallback(
    async (rotate = false): Promise<string | null> => {
      if (!itineraryId) return null;
      try {
        const res = await shareItineraryApi(itineraryId, rotate);
        setShareToken(res.share_token);
        return res.share_token;
      } catch (e) {
        logger.warn('[itinerary] shareItinerary failed', e);
        return null;
      }
    },
    [itineraryId],
  );

  // Fetch the roster for the active trip (owner + members). Also settles the
  // caller's own role from the row flagged is_owner=false matching userId, or
  // 'owner' when none matches.
  const refreshMembers = useCallback(async (): Promise<EmbedMember[]> => {
    if (!itineraryId) return [];
    try {
      const list = await listMembersApi(itineraryId);
      setMembers(list);
      const mine = list.find(m => m.user_id === userId);
      if (mine) setMyRole(mine.role);
      return list;
    } catch (e) {
      logger.warn('[itinerary] refreshMembers failed', e);
      return [];
    }
  }, [itineraryId, userId]);

  // Mint a collaboration invite link with a role; returns the token (the caller
  // builds the join URL). Owner/editor only server-side.
  const createInviteLink = useCallback(
    async (role: 'editor' | 'viewer'): Promise<string | null> => {
      if (!itineraryId) return null;
      try {
        const res = await createInviteApi(itineraryId, role);
        return res.token;
      } catch (e) {
        logger.warn('[itinerary] createInviteLink failed', e);
        return null;
      }
    },
    [itineraryId],
  );

  // Claim an invite and make the joined trip the ACTIVE itinerary. The embed
  // shows one trip at a time, so joining switches to the shared trip (the
  // visitor's own trip is untouched server-side; a full trip switcher is a
  // future step). Registration is enforced server-side (guests get 403).
  // Make a given itinerary the ACTIVE one: hydrate its items/title/profile and
  // settle role/roster. Shared by joinTrip and switchTrip.
  const activateItinerary = useCallback(
    async (id: string, role: EmbedItineraryRole) => {
      setItineraryId(id);
      itineraryOwnerRef.current = userId;
      setMyRole(role);
      const { itinerary, items: wire } = await getItineraryWithItems(id);
      const meta =
        (itinerary?.metadata as Record<string, unknown> | undefined) || {};
      itineraryMetadataRef.current = { ...meta };
      setTripProfile((meta.trip_profile as TripProfile | undefined) || {});
      if (itinerary?.title) setTitle(itinerary.title);
      setShareToken(itinerary?.share_token ?? null);
      setItems(wire.map(fromWire));
      setHydrated(true);
      try {
        setMembers(await listMembersApi(id));
      } catch {
        /* roster is best-effort */
      }
    },
    [userId],
  );

  // Fetch the visitor's trips (owned + joined) for the switcher. No-op when the
  // instance hasn't enabled switching (keeps single-trip instances lean).
  const refreshTrips = useCallback(async (): Promise<EmbedTripSummary[]> => {
    if (!switcherEnabled) return [];
    try {
      const trips = await listMembershipsApi();
      setAvailableTrips(trips);
      return trips;
    } catch (e) {
      logger.warn('[itinerary] refreshTrips failed', e);
      return [];
    }
  }, [switcherEnabled]);

  // Switch the active trip (switcher UI). Uses the role from the trip list.
  const switchTrip = useCallback(
    async (id: string) => {
      if (!switcherEnabled || id === itineraryId) return;
      const t = availableTrips.find(x => x.itinerary_id === id);
      await activateItinerary(id, t?.role ?? 'viewer');
    },
    [switcherEnabled, itineraryId, availableTrips, activateItinerary],
  );

  const joinTrip = useCallback(
    async (
      token: string,
    ): Promise<{ role: EmbedItineraryRole; switched: boolean } | null> => {
      try {
        const res = await joinItineraryApi(token);
        // Only switch into the joined trip when this instance supports multiple
        // trips. Otherwise the membership is recorded server-side but the active
        // (own) trip is untouched — "not switch" for single-trip instances.
        if (switcherEnabled) {
          await activateItinerary(res.itinerary_id, res.role);
          void refreshTrips();
        }
        return { role: res.role, switched: switcherEnabled };
      } catch (e) {
        logger.warn('[itinerary] joinTrip failed', e);
        return null;
      }
    },
    [switcherEnabled, activateItinerary, refreshTrips],
  );

  // Promote a personal pick to the group plan (or demote). Optimistic via the
  // shared updateItem path (which sends scope in the PUT).
  const setItemScope = useCallback(
    (id: string, scope: ItineraryItemScope) => {
      updateItem(id, { scope });
    },
    [updateItem],
  );

  const updateTripProfile = useCallback(
    (patch: Partial<TripProfile>) => {
      setTripProfile(prev => {
        const next: TripProfile = {
          ...prev,
          ...patch,
          contact: { ...(prev.contact || {}), ...(patch.contact || {}) },
          party: patch.party
            ? { ...(prev.party || { adults: 2, children: 0 }), ...patch.party }
            : prev.party,
        };
        // Persist in background. Merge with the rest of metadata so we
        // don't clobber fields the brain or other surfaces wrote.
        if (itineraryId) {
          const merged = {
            ...itineraryMetadataRef.current,
            trip_profile: next,
          };
          itineraryMetadataRef.current = merged;
          (async () => {
            try {
              await updateItinerary(itineraryId, { metadata: merged });
            } catch (e) {
              logger.warn('[itinerary] updateTripProfile persist failed', e);
            }
          })();
        }
        return next;
      });
    },
    [itineraryId],
  );

  const counts = useMemo<Record<ItineraryItemStatus, number>>(() => {
    const c: Record<ItineraryItemStatus, number> = {
      suggested: 0,
      saved: 0,
      pending_pay: 0,
      confirmed: 0,
      cancelled: 0,
    };
    for (const it of items) c[it.status] += 1;
    return c;
  }, [items]);

  const pendingCount = counts.pending_pay;

  const flushPendingAdds = useCallback((): number => {
    const toAdd = pendingAdds;
    if (!toAdd.length) return 0;
    setPendingAdds([]);
    for (const input of toAdd) addItem(input);
    return toAdd.length;
  }, [pendingAdds, addItem]);

  // Post-registration flush runs DECLARATIVELY: right after register() the
  // auth event can reset/re-run the itinerary bootstrap, so an imperative
  // flush from AuthView races itineraryId and the POSTs get dropped
  // (optimistic-only ghosts). Waiting for REGISTERED ('authenticated' ≠
  // guest — both are authed sessions) + a live itineraryId makes the flush
  // land whenever bootstrap settles.
  //
  // OWNERSHIP GUARD: on sign-in to an EXISTING account the userId changes
  // in the same commit that authState flips, while itineraryId still points
  // at the guest's itinerary — flushing then posts into an itinerary the
  // new session doesn't own and the items silently vanish after the
  // account's own bootstrap replaces state. itineraryOwnerRef is written
  // synchronously by the bootstrap, so requiring owner === current user
  // holds the flush until the RIGHT itinerary is live.
  useEffect(() => {
    if (authState !== 'authenticated') return;
    if (!itineraryId || !pendingAdds.length) return;
    if (itineraryOwnerRef.current !== userId) return;
    logger.info('[itinerary] flushing pending adds post-auth', {
      count: pendingAdds.length,
      itinerary_id: itineraryId,
    });
    flushPendingAdds();
  }, [
    authState,
    itineraryId,
    userId,
    pendingAdds.length,
    flushPendingAdds,
    pendingAdds,
  ]);

  const value = useMemo(
    () => ({
      items,
      hydrated,
      addItem,
      pendingAdds,
      queuePendingAdd,
      flushPendingAdds,
      clearPendingAdds,
      updateItem,
      startCheckout,
      markConfirmed,
      removeItem,
      counts,
      pendingCount,
      title,
      renameItinerary,
      shareToken,
      shareItinerary,
      myUserId: userId,
      myRole,
      members,
      refreshMembers,
      createInviteLink,
      setItemScope,
      joinTrip,
      switcherEnabled,
      availableTrips,
      refreshTrips,
      switchTrip,
      tripProfile,
      updateTripProfile,
    }),
    [
      items,
      hydrated,
      addItem,
      pendingAdds,
      queuePendingAdd,
      flushPendingAdds,
      clearPendingAdds,
      updateItem,
      startCheckout,
      markConfirmed,
      removeItem,
      counts,
      pendingCount,
      title,
      renameItinerary,
      shareToken,
      shareItinerary,
      userId,
      myRole,
      members,
      refreshMembers,
      createInviteLink,
      setItemScope,
      joinTrip,
      switcherEnabled,
      availableTrips,
      refreshTrips,
      switchTrip,
      tripProfile,
      updateTripProfile,
    ],
  );

  return (
    <ItineraryContext.Provider value={value}>
      {children}
    </ItineraryContext.Provider>
  );
};

export function useItinerary(): ItineraryContextValue {
  const ctx = useContext(ItineraryContext);
  if (!ctx)
    throw new Error('useItinerary must be used within ItineraryProvider');
  return ctx;
}
